"use client";

import {
  createContext,
  type Dispatch,
  type PropsWithChildren,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

type ThemeName = "light" | "dark";

type ThemeProviderProps = PropsWithChildren<{
  storageKey?: string;
  forcedTheme?: ThemeName;
}>;

type ThemeContextValue = {
  themes: ThemeName[];
  forcedTheme?: ThemeName;
  theme: ThemeName;
  resolvedTheme: ThemeName;
  setTheme: Dispatch<SetStateAction<string>>;
};

const DEFAULT_THEME: ThemeName = "light";
const THEMES: ThemeName[] = ["light", "dark"];

const ThemeContext = createContext<ThemeContextValue>({
  themes: THEMES,
  theme: DEFAULT_THEME,
  resolvedTheme: DEFAULT_THEME,
  setTheme: () => undefined,
});

function isValidTheme(value: string | null | undefined): value is ThemeName {
  return value === "light" || value === "dark";
}

function normalizeTheme(value: string | null | undefined): ThemeName {
  return isValidTheme(value) ? value : DEFAULT_THEME;
}

function applyTheme(theme: ThemeName) {
  const root = document.documentElement;
  root.classList.remove(...THEMES);
  root.classList.add(theme);
  root.style.colorScheme = theme;
}

export function ThemeProvider({
  children,
  storageKey = "feedvote-theme",
  forcedTheme,
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<ThemeName>(DEFAULT_THEME);

  useEffect(() => {
    if (forcedTheme || typeof window === "undefined") return;

    const savedTheme = window.localStorage.getItem(storageKey);
    const nextTheme = normalizeTheme(savedTheme);
    setThemeState(nextTheme);

    if (!isValidTheme(savedTheme)) {
      window.localStorage.setItem(storageKey, nextTheme);
    }
  }, [forcedTheme, storageKey]);

  const resolvedTheme = forcedTheme ?? theme;

  useEffect(() => {
    applyTheme(resolvedTheme);
  }, [resolvedTheme]);

  const setTheme = useCallback<Dispatch<SetStateAction<string>>>(
    (value) => {
      setThemeState((current) => {
        const rawNext = typeof value === "function" ? value(current) : value;
        const nextTheme = normalizeTheme(rawNext);

        // Update the root before React's next paint so theme changes feel
        // immediate even on portals with long roadmaps or feedback lists.
        applyTheme(nextTheme);

        try {
          window.localStorage.setItem(storageKey, nextTheme);
        } catch {
          // Ignore storage failures in private browsing or restricted contexts.
        }

        return nextTheme;
      });
    },
    [storageKey],
  );

  const contextValue = useMemo<ThemeContextValue>(
    () => ({
      themes: THEMES,
      forcedTheme,
      theme,
      resolvedTheme,
      setTheme,
    }),
    [forcedTheme, theme, resolvedTheme, setTheme],
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
